import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Camera, Upload, CheckCircle2, Clock, XCircle, Shield,
  Loader2, UserCheck, FileText, AlertCircle, ArrowLeft
} from "lucide-react";

// Constants
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

export default function VerificationPage() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  
  const [selfieImage, setSelfieImage] = useState(null);
  const [idImage, setIdImage] = useState(null);
  const [idType, setIdType] = useState("");
  
  const selfieInputRef = useRef(null);
  const idInputRef = useRef(null);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (error) {
      toast.error("Failed to fetch verification status");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user?.user_type !== "professional") {
      navigate("/");
      return;
    }
    fetchVerificationStatus();
  }, [user, navigate, fetchVerificationStatus]);

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error(`Image must be less than ${MAX_IMAGE_SIZE_MB}MB`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "selfie") {
        setSelfieImage(reader.result);
      } else {
        setIdImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!selfieImage || !idImage || !idType) {
      toast.error("Please upload both photos and select ID type");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/verification/submit`,
        {
          selfie_image: selfieImage,
          id_document_image: idImage,
          id_type: idType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Verification submitted! We'll review it within 24-48 hours.");
      await refreshUser();
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit verification");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E40AF]" />
      </div>
    );
  }

  const isVerified = verificationStatus?.is_verified;
  const isPending = verificationStatus?.verification_status === "pending";
  const isRejected = verificationStatus?.verification_status === "rejected";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-3xl mx-auto" data-testid="verification-page">
        <button
          onClick={() => navigate("/pro/dashboard")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1E40AF] mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            Identity Verification
          </h1>
          <p className="text-gray-600">
            Verify your identity to build trust with customers
          </p>
        </div>

        {/* Already Verified */}
        {isVerified && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading font-bold text-xl text-green-800 mb-2">
                You're Verified!
              </h2>
              <p className="text-green-700">
                Your identity has been verified. Customers will see a verified badge on your profile.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pending Review */}
        {isPending && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="font-heading font-bold text-xl text-yellow-800 mb-2">
                Verification Pending
              </h2>
              <p className="text-yellow-700">
                Your verification is being reviewed. This usually takes 24-48 hours.
              </p>
              <p className="text-sm text-yellow-600 mt-2">
                Submitted: {verificationStatus?.verification?.submitted_at 
                  ? new Date(verificationStatus.verification.submitted_at).toLocaleDateString()
                  : 'Recently'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Rejected */}
        {isRejected && (
          <Card className="border-red-200 bg-red-50 mb-6">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-red-800 mb-1">
                    Verification Rejected
                  </h3>
                  <p className="text-red-700 text-sm mb-2">
                    Your previous verification was not approved. Please submit again with clear photos.
                  </p>
                  {verificationStatus?.verification?.reviewer_notes && (
                    <p className="text-sm text-red-600 bg-red-100 p-2 rounded">
                      Note: {verificationStatus.verification.reviewer_notes}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Verification Form */}
        {!isVerified && !isPending && (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                Submit Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Requirements
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Take a clear selfie photo showing your face</li>
                  <li>• Upload a photo of your valid ID (passport, driver's license, or national ID)</li>
                  <li>• Make sure both photos are well-lit and not blurry</li>
                  <li>• Your face in the selfie must match your ID photo</li>
                </ul>
              </div>

              {/* Selfie Upload */}
              <div className="space-y-3">
                <Label className="text-base font-medium">1. Selfie Photo</Label>
                <div
                  onClick={() => selfieInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    selfieImage ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-[#1E40AF]"
                  }`}
                >
                  {selfieImage ? (
                    <div className="space-y-3">
                      <img
                        src={selfieImage}
                        alt="Selfie preview"
                        className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-white shadow"
                      />
                      <p className="text-green-600 font-medium flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Selfie uploaded
                      </p>
                      <p className="text-sm text-gray-500">Click to change</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-600">Click to upload your selfie</p>
                      <p className="text-sm text-gray-400">JPG, PNG (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={selfieInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, "selfie")}
                  data-testid="selfie-input"
                />
              </div>

              {/* ID Type Selection */}
              <div className="space-y-3">
                <Label className="text-base font-medium">2. ID Document Type</Label>
                <Select value={idType} onValueChange={setIdType}>
                  <SelectTrigger className="h-12" data-testid="id-type-select">
                    <SelectValue placeholder="Select your ID type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drivers_license">Driver's License</SelectItem>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="national_id">National ID Card</SelectItem>
                    <SelectItem value="state_id">State ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ID Upload */}
              <div className="space-y-3">
                <Label className="text-base font-medium">3. ID Document Photo</Label>
                <div
                  onClick={() => idInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    idImage ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-[#1E40AF]"
                  }`}
                >
                  {idImage ? (
                    <div className="space-y-3">
                      <img
                        src={idImage}
                        alt="ID preview"
                        className="max-w-[200px] max-h-[150px] object-contain mx-auto rounded-lg border shadow"
                      />
                      <p className="text-green-600 font-medium flex items-center justify-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        ID document uploaded
                      </p>
                      <p className="text-sm text-gray-500">Click to change</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto">
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-600">Click to upload your ID document</p>
                      <p className="text-sm text-gray-400">JPG, PNG (max 5MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={idInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, "id")}
                  data-testid="id-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!selfieImage || !idImage || !idType || submitting}
                className="w-full h-12 bg-[#1E40AF] hover:bg-[#1E3A8A] rounded-full text-base"
                data-testid="submit-verification-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Submit Verification
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Your photos are securely stored and only used for verification purposes.
                We typically review submissions within 24-48 hours.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
}
